import React, { useEffect, useRef, useState } from 'react';
import { RootState } from 'features/store';
import { getUser } from 'features/auth/auth.slice';
import './Tabs.scss';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

type Tab = {
    id: number;
    label: string;
    component: any;
    role?: string[];
    link?: string;
};

type TabsProps = {
    tabs: Tab[];
    activeTab?: number;
    linkPrefix?: string;
    errorMessage?: string;
    onTabChange?: (id: number) => void;
    params?: {
        [key: string]: string | number;
    };
};

export const Tabs: React.FC<React.PropsWithChildren & TabsProps> = ({ tabs, activeTab, onTabChange, linkPrefix, params, errorMessage }) => {
    const [firstRender, setFirstRender] = useState(true);
    const underlineRef = useRef<HTMLLIElement>(null);
    const [activeRoute, setActiveRoute] = useState(activeTab ? null : 0);
    const user = useSelector((state: RootState) => getUser(state));
    const location = useLocation();
    let tabLink = '#';


    window.addEventListener('resize', () => underlineRef.current && animateUnderline(underlineRef.current as HTMLLIElement))

    const isAllowed = (tab: Tab) => {
        if (!tab.role) return true;
        return tab.role.includes(user?.role || '');
    };

    const tabsItems = tabs.filter(isAllowed).map((tab, index) => {
        if (tab.link) {
            tabLink = linkPrefix ? '/app/' + linkPrefix + tab.link : tab.link;
            tabLink = replaceParams(tabLink);
        }

        return (
            <span className={`tab${tabLink === 'false' ? ' disabled' : ''}`} key={'tab-' + index} id={`tab-${index}`} /* WTF ? title={errorMessage}*/>
                <Link
                    to={tabLink}
                    onClick={() => {
                        if (activeTab !== undefined) {
                            onTabChange && onTabChange(index);
                        } else {
                            setActiveRoute(index);
                        }
                    }}
                >
                    {tab.label}
                </Link>
            </span>
        );
    });

    function animateUnderline(underline: HTMLLIElement) {
        const activeTabEl = document.querySelector(`#tab-${activeTab ?? activeRoute}`) as HTMLElement;
        const parentEl = activeTabEl.parentElement as HTMLElement;

        underline.style.width = `${activeTabEl.clientWidth}px`;
        underline.style.left = `${activeTabEl.getBoundingClientRect().left - parentEl.getBoundingClientRect().left}px`;
    }

    function replaceParams(link: string) {
        if (!params) return link;
        let isValid = true;

        let paramsEntries = Object.entries(params);
        paramsEntries.forEach(([key, value]) => {
            if (value === null && link.includes(key)) return (isValid = false);
            link = link.replace(':' + key, value?.toString());
        });

        return isValid ? link : 'false';
    }

    useEffect(() => {
        if (activeTab) return;

        let tabIndex = tabs.findIndex(({ link }) => {
            if (!link) return '#';
            return location.pathname.endsWith(replaceParams(link));
        });

        setActiveRoute(tabIndex === -1 ? 0 : tabIndex);
    }, [activeRoute]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        if (underlineRef.current && firstRender) {
            timeout = setTimeout(() => animateUnderline(underlineRef.current as HTMLLIElement), 100);
            setFirstRender(false);
        } else {
            animateUnderline(underlineRef.current as HTMLLIElement);
        }

        return () => {
            timeout && clearTimeout(timeout);
        };
    }, [activeTab, activeRoute]);

    return (
        <>
            <ul className="tabs">
                {tabsItems}
                <li className="underline" ref={underlineRef}></li>
            </ul>
            {tabs[activeTab ?? activeRoute ?? 0].component}
        </>
    );
};
